import discountQueries from '@apis/queries/discountQueries';
import { useFlow } from '@stackflow/react';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

/** 선물 선택 상태와 적용 */
function useOrderGift() {
    const { pop } = useFlow();
    const dispatch = useDispatch();
    const {
        shopId, giftPayments, totalAmount, prepaidCardAmount, totalDiscount,
    } = useCalculatedRequest();
    const { data } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });
    const gifts = data?.gifts;

    const [selectedGiftIds, setSelectedGiftIds] = useState<string[]>(
        () => giftPayments?.map((payment) => payment.giftCouponId) ?? [],
    );

    const nextPayments = useMemo(() => {
        let leftAmount = totalAmount - prepaidCardAmount - totalDiscount;
        // 선택 순서를 보장하기 위해 선택 배열을 기준으로 매핑
        return selectedGiftIds.map((id) => {
            const gift = gifts?.find((it) => it.id === id)!;
            // 순회하며 금액을 최소 금액을 계산
            const { discountValue, couponType } = gift;
            const prevAmount = leftAmount;
            if (leftAmount > discountValue) {
                leftAmount -= discountValue;
            }
            return {
                giftCouponId: id,
                amount: Math.min(prevAmount, discountValue),
                type: couponType,
            };
        });
    }, [totalAmount, prepaidCardAmount, totalDiscount, selectedGiftIds, gifts]);

    const draftGiftAmount = nextPayments
        .reduce((sum, gift) => sum + gift.amount, 0) ?? 0;
    const alreadyBalanceSelected = !!nextPayments.find((it) => it.type === 'BALANCE');

    const toggleGift = (giftId: string) => {
        setSelectedGiftIds((prev) => (
            prev.includes(giftId)
                ? prev.filter((id) => id !== giftId)
                : [...prev, giftId]
        ));
    };

    const handleApply = () => {
        dispatch(orderActions.setGiftPayments(nextPayments));
        pop();
    };

    return {
        gifts,
        selectedGiftIds,
        draftGiftAmount,
        alreadyBalanceSelected,
        toggleGift,
        handleApply,
        handleClose: pop,
    };
}

export default useOrderGift;
